'use client';

import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
  type FC,
  type ReactNode,
} from 'react';

type DesktopContentWidth = 'column' | 'full';

interface DesktopContentWidthValue {
  width: DesktopContentWidth;
  registerFullBleed: () => void;
  unregisterFullBleed: () => void;
}

const DesktopContentWidthContext = createContext<DesktopContentWidthValue>({
  width: 'column',
  registerFullBleed: () => undefined,
  unregisterFullBleed: () => undefined,
});

export const DesktopContentWidthProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [fullBleedCount, setFullBleedCount] = useState(0);

  const registerFullBleed = useCallback(() => setFullBleedCount((n) => n + 1), []);
  const unregisterFullBleed = useCallback(() => setFullBleedCount((n) => Math.max(0, n - 1)), []);

  const value = useMemo<DesktopContentWidthValue>(
    () => ({
      width: fullBleedCount > 0 ? 'full' : 'column',
      registerFullBleed,
      unregisterFullBleed,
    }),
    [fullBleedCount, registerFullBleed, unregisterFullBleed]
  );

  return (
    <DesktopContentWidthContext.Provider value={value}>{children}</DesktopContentWidthContext.Provider>
  );
};

export function useDesktopContentWidth(): DesktopContentWidth {
  return useContext(DesktopContentWidthContext).width;
}

export const DesktopFullBleed: FC = () => {
  const { registerFullBleed, unregisterFullBleed } = useContext(DesktopContentWidthContext);

  useLayoutEffect(() => {
    registerFullBleed();
    return unregisterFullBleed;
  }, [registerFullBleed, unregisterFullBleed]);

  return null;
};
